import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Focus, Hand, MousePointer2, Plus, RotateCcw, Trash2, X, ZoomIn, ZoomOut } from 'lucide-react';
import { photoUrl } from '../api';
import type { LibraryPhoto, Point, ScreenMapping } from '../types';

type Props = {
  photo: LibraryPhoto;
  onClose: () => void;
  onSave: (screens: ScreenMapping[]) => Promise<void>;
};

export function MappingEditor({ photo, onClose, onSave }: Props) {
  const [screens, setScreens] = useState<ScreenMapping[]>(photo.screens);
  const [active, setActive] = useState<string | null>(photo.screens[0]?.id ?? null);
  const [draft, setDraft] = useState<Point[]>([]);
  const [draftKind, setDraftKind] = useState<'screen' | 'reflection-removal'>('screen');
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panMode, setPanMode] = useState(false);
  const [panning, setPanning] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panStart = useRef({ pointerId: -1, x: 0, y: 0, left: 0, top: 0 });
  const activeScreen = useMemo(() => screens.find((screen) => screen.id === active), [active, screens]);

  useEffect(() => setScreens(photo.screens), [photo]);

  function svgPoint(event: React.PointerEvent<SVGSVGElement>): Point {
    const svg = svgRef.current!;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const transformed = point.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: transformed.x, y: transformed.y };
  }

  function addPoint(event: React.PointerEvent<SVGSVGElement>) {
    if (panMode || (event.target as Element).closest('[data-handle]') || draft.length >= 4) return;
    const next = [...draft, svgPoint(event)];
    if (next.length === 4) {
      if (draftKind === 'reflection-removal' && active) {
        setScreens((current) => current.map((screen) => screen.id === active ? {
          ...screen,
          reflection: { corners: next as ScreenMapping['corners'] },
        } : screen));
        setDraft([]);
        setDraftKind('screen');
        return;
      }
      const screen: ScreenMapping = {
        id: `screen-${crypto.randomUUID().slice(0, 8)}`,
        corners: next as ScreenMapping['corners'],
        bleed: 0.008,
      };
      setScreens((current) => [...current, screen]);
      setActive(screen.id);
      setDraft([]);
    } else {
      setDraft(next);
    }
  }

  function changeZoom(next: number) {
    const scroll = scrollRef.current;
    const clamped = Math.max(1, Math.min(4, next));
    if (!scroll) return setZoom(clamped);
    const x = (scroll.scrollLeft + scroll.clientWidth / 2) / Math.max(1, scroll.scrollWidth);
    const y = (scroll.scrollTop + scroll.clientHeight / 2) / Math.max(1, scroll.scrollHeight);
    setZoom(clamped);
    if (clamped === 1) setPanMode(false);
    requestAnimationFrame(() => {
      scroll.scrollLeft = x * scroll.scrollWidth - scroll.clientWidth / 2;
      scroll.scrollTop = y * scroll.scrollHeight - scroll.clientHeight / 2;
    });
  }

  function startPan(event: React.PointerEvent<HTMLDivElement>) {
    const scroll = scrollRef.current;
    if (!panMode || zoom <= 1 || !scroll) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    panStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: scroll.scrollLeft, top: scroll.scrollTop };
    setPanning(true);
  }

  function movePan(event: React.PointerEvent<HTMLDivElement>) {
    const scroll = scrollRef.current;
    if (!scroll || panStart.current.pointerId !== event.pointerId) return;
    scroll.scrollLeft = panStart.current.left - (event.clientX - panStart.current.x);
    scroll.scrollTop = panStart.current.top - (event.clientY - panStart.current.y);
  }

  function endPan(event: React.PointerEvent<HTMLDivElement>) {
    if (panStart.current.pointerId !== event.pointerId) return;
    panStart.current.pointerId = -1;
    setPanning(false);
  }

  function movePoint(screenId: string, index: number, event: React.PointerEvent<SVGCircleElement>, target: 'screen' | 'reflection-removal' = 'screen') {
    event.currentTarget.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) => {
      const svg = svgRef.current;
      if (!svg) return;
      const point = svg.createSVGPoint();
      point.x = moveEvent.clientX;
      point.y = moveEvent.clientY;
      const next = point.matrixTransform(svg.getScreenCTM()!.inverse());
      setScreens((current) => current.map((screen) => {
        if (screen.id !== screenId) return screen;
        const sourceCorners = target === 'reflection-removal' ? screen.reflection?.corners : screen.corners;
        if (!sourceCorners) return screen;
        const corners = [...sourceCorners] as ScreenMapping['corners'];
        corners[index] = { x: Math.max(0, Math.min(photo.width, next.x)), y: Math.max(0, Math.min(photo.height, next.y)) };
        return target === 'reflection-removal'
          ? { ...screen, reflection: { corners } }
          : { ...screen, corners };
      }));
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }

  async function save() {
    setSaving(true);
    try { await onSave(screens); } finally { setSaving(false); }
  }

  return (
    <div className="editor-shell">
      <div className="editor-topbar">
        <div>
          <span className="eyebrow">Admin mapping</span>
          <h2>{photo.name}</h2>
          <p>Click corners clockwise: top-left, top-right, bottom-right, bottom-left.</p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close editor"><X /></button>
      </div>
      <div className="editor-layout">
        <aside className="mapping-sidebar">
          <div className="instruction-card">
            <MousePointer2 size={20} />
            <div><strong>{draft.length ? `${draftKind === 'reflection-removal' ? 'Removal area' : 'Screen'} corner ${draft.length + 1} of 4` : draftKind === 'reflection-removal' ? 'Remove the old reflection' : 'Add a screen'}</strong><span>{draftKind === 'reflection-removal' ? 'Outline the old advert reflection or shadow. No new reflection will be added.' : 'Click precisely on the inner display edge.'}</span></div>
          </div>
          <button className="btn secondary wide" type="button" onClick={() => { setDraft([]); setDraftKind('screen'); setActive(null); }}>
            <Plus size={17} /> Add screen
          </button>
          {draft.length > 0 && (
            <button className="btn ghost wide" type="button" onClick={() => setDraft([])}><RotateCcw size={16} /> Reset points</button>
          )}
          <div className="screen-list">
            {screens.map((screen, index) => (
              <button type="button" key={screen.id} className={screen.id === active ? 'active' : ''} onClick={() => { setActive(screen.id); setDraft([]); }}>
                <span>Screen {index + 1}</span><Check size={15} />
              </button>
            ))}
          </div>
          {activeScreen && (
            <>
              <label className="bleed-control">
                <span><strong>Edge cover</strong><small>{Math.round((activeScreen.bleed ?? 0.006) * 1000) / 10}%</small></span>
                <input type="range" min="0" max="0.02" step="0.002" value={activeScreen.bleed ?? 0.006} onChange={(event) => setScreens((current) => current.map((screen) => screen.id === active ? { ...screen, bleed: Number(event.target.value) } : screen))} />
                <small>Extends the creative slightly under the bezel to hide fine gaps.</small>
              </label>
              {!activeScreen.reflection ? (
                <button className="btn ghost wide" type="button" onClick={() => { setDraft([]); setDraftKind('reflection-removal'); }}>
                  <MousePointer2 size={16} /> Remove old reflection <small>(optional)</small>
                </button>
              ) : (
                <div className="bleed-control">
                  <span><strong>Old reflection removal</strong><small>Mapped</small></span>
                  <small>The original advert reflection will be neutralized. The new creative will not be reflected.</small>
                  <button className="text-button" type="button" onClick={() => setScreens((current) => current.map((screen) => screen.id === active ? { ...screen, reflection: undefined } : screen))}>Remove this area</button>
                </div>
              )}
              <button className="btn danger-text wide" type="button" onClick={() => { setScreens((current) => current.filter((screen) => screen.id !== active)); setActive(null); }}>
                <Trash2 size={16} /> Delete selected screen
              </button>
            </>
          )}
          <div className="editor-actions">
            <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="button" onClick={save} disabled={saving || !!draft.length}>{saving ? 'Saving…' : 'Save mappings'}</button>
          </div>
        </aside>
        <div className="mapping-stage">
          <div className="zoom-toolbar" aria-label="Mapping zoom controls">
            <button type="button" onClick={() => changeZoom(zoom - 0.25)} disabled={zoom <= 1} aria-label="Zoom out"><ZoomOut size={17} /></button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => changeZoom(zoom + 0.25)} disabled={zoom >= 4} aria-label="Zoom in"><ZoomIn size={17} /></button>
            <button type="button" className={panMode ? 'active' : ''} onClick={() => setPanMode((value) => !value)} disabled={zoom <= 1} aria-label="Pan image" title="Drag the image to pan"><Hand size={17} /></button>
            <button type="button" onClick={() => changeZoom(1)} aria-label="Fit image"><Focus size={17} /></button>
          </div>
          <div ref={scrollRef} className={`mapping-scroll${panMode ? ' pan-enabled' : ''}${panning ? ' panning' : ''}`} onPointerDown={startPan} onPointerMove={movePan} onPointerUp={endPan} onPointerCancel={endPan}>
            <div className="mapping-canvas" style={{ width: `${zoom * 100}%` }}>
              <svg
                ref={svgRef}
                viewBox={`0 0 ${photo.width} ${photo.height}`}
                onPointerDown={addPoint}
                aria-label="Screen mapping canvas"
              >
            <image href={photoUrl(photo)} x="0" y="0" width={photo.width} height={photo.height} />
            {screens.map((screen, screenIndex) => (
              <g key={screen.id}>
                <g className={screen.id === active ? 'mapping active' : 'mapping'} onPointerDown={(event) => { event.stopPropagation(); setActive(screen.id); setDraftKind('screen'); }}>
                  <polygon points={screen.corners.map((point) => `${point.x},${point.y}`).join(' ')} />
                  <text x={screen.corners[0].x} y={screen.corners[0].y - 14}>Screen {screenIndex + 1}</text>
                  {screen.corners.map((point, pointIndex) => (
                    <g key={pointIndex} data-handle>
                      <circle className="handle-hit" cx={point.x} cy={point.y} r={Math.max(photo.width, photo.height) / 105 / zoom} onPointerDown={(event) => { event.stopPropagation(); setActive(screen.id); movePoint(screen.id, pointIndex, event); }} />
                      <circle className="handle-dot" cx={point.x} cy={point.y} r={Math.max(photo.width, photo.height) / 380 / zoom} />
                    </g>
                  ))}
                </g>
                {screen.reflection && (
                  <g className="reflection-mapping" onPointerDown={(event) => { event.stopPropagation(); setActive(screen.id); setDraftKind('reflection-removal'); }}>
                    <polygon points={screen.reflection.corners.map((point) => `${point.x},${point.y}`).join(' ')} />
                    <text x={screen.reflection.corners[0].x} y={screen.reflection.corners[0].y - 14}>Remove old reflection {screenIndex + 1}</text>
                    {screen.reflection.corners.map((point, pointIndex) => (
                      <g key={pointIndex} data-handle>
                        <circle className="handle-hit" cx={point.x} cy={point.y} r={Math.max(photo.width, photo.height) / 105 / zoom} onPointerDown={(event) => { event.stopPropagation(); setActive(screen.id); movePoint(screen.id, pointIndex, event, 'reflection-removal'); }} />
                        <circle className="handle-dot" cx={point.x} cy={point.y} r={Math.max(photo.width, photo.height) / 380 / zoom} />
                      </g>
                    ))}
                  </g>
                )}
              </g>
            ))}
            {draft.length > 0 && (
              <g className="draft-mapping">
                <polyline points={draft.map((point) => `${point.x},${point.y}`).join(' ')} />
                {draft.map((point, index) => <circle key={index} cx={point.x} cy={point.y} r={Math.max(photo.width, photo.height) / 380 / zoom} />)}
              </g>
            )}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
